import { useState } from 'react';
import { Search, Pill, Eye, AlertCircle, Calendar, RefreshCw, Send } from 'lucide-react';
import { format, isAfter, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Pagination } from '../components/ui/pagination';
import { LoadingTable } from '../components/ui/loading-states';
import { ErrorState } from '../components/ui/error-state';
import { useMedicationScans, useRetryMedicationScan, useRetryPendingMedicationScans } from '../hooks/useMedications';
import { useEmployees } from '../hooks/useEmployees';
import type {
  MedicationScan,
  MedicationScanFilters,
} from '../types/medication.types';
import { SyncStatus } from '../types/medication.types';

export function Medications() {
  // State для фильтров
  const [filters, setFilters] = useState<MedicationScanFilters>({
    page: 1,
    page_size: 20,
    sort: 'scanned_at:desc',
  });

  // State для инпутов (отдельно от фильтров для debounce)
  const [searchGtin, setSearchGtin] = useState('');
  const [searchPatientIin, setSearchPatientIin] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('all');
  const [selectedSyncStatus, setSelectedSyncStatus] = useState<string>('all');

  // State для просмотра деталей
  const [selectedScan, setSelectedScan] = useState<MedicationScan | null>(null);

  // Data fetching
  const { data, isLoading, error, refetch } = useMedicationScans(filters);
  const {
    data: employeesData,
    isLoading: isLoadingEmployees,
  } = useEmployees({ page: 1, limit: 100, isActive: true });

  // Mutation для retry
  const retryMutation = useRetryMedicationScan();
  const retryPendingMutation = useRetryPendingMedicationScans();

  // Применить фильтры
  const handleApplyFilters = () => {
    setFilters({
      ...filters,
      gtin: searchGtin || undefined,
      patient_iin: searchPatientIin || undefined,
      employee_id: selectedEmployeeId !== 'all' ? selectedEmployeeId : undefined,
      sync_status: selectedSyncStatus !== 'all' ? selectedSyncStatus : undefined,
      from: dateFrom || undefined,
      to: dateTo || undefined,
      page: 1, // Сбросить на первую страницу при фильтрации
    });
  };

  // Сбросить фильтры
  const handleResetFilters = () => {
    setSearchGtin('');
    setSearchPatientIin('');
    setSelectedEmployeeId('all');
    setSelectedSyncStatus('all');
    setDateFrom('');
    setDateTo('');
    setFilters({
      page: 1,
      page_size: 20,
      sort: 'scanned_at:desc',
    });
  };

  // Обработка повторной отправки в 1С
  const handleRetrySync = (scanId: string) => {
    if (confirm('Повторить отправку сканирования в 1С?')) {
      retryMutation.mutate(scanId, {
        onSuccess: () => {
          alert('Повторная отправка запущена');
          refetch();
        },
        onError: (error: any) => {
          alert(`Ошибка: ${error?.message || 'Не удалось повторить отправку'}`);
        },
      });
    }
  };

  // Обработка отправки всех неотправленных записей в 1С
  const handleRetryPending = () => {
    if (confirm('Отправить все неотправленные записи (pending и failed) в 1С?')) {
      retryPendingMutation.mutate(undefined, {
        onSuccess: (result) => {
          alert(result.message || `Отправлено ${result.retried} записей`);
          refetch();
        },
        onError: (error: any) => {
          alert(`Ошибка: ${error?.message || 'Не удалось отправить записи'}`);
        },
      });
    }
  };

  // Обработка пагинации
  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  // Проверка истечения срока годности
  const isExpired = (expiryDate?: string): boolean => {
    if (!expiryDate) return false;
    try {
      return isAfter(new Date(), parseISO(expiryDate));
    } catch {
      return false;
    }
  };

  // Форматирование даты/времени
  const formatDateTime = (dateString: string): string => {
    try {
      return format(parseISO(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
    } catch {
      return dateString;
    }
  };

  // Форматирование даты (только дата)
  const formatDate = (dateString: string): string => {
    try {
      return format(parseISO(dateString), 'dd.MM.yyyy', { locale: ru });
    } catch {
      return dateString;
    }
  };

  // Определение variant для Badge по статусу синхронизации
  const getSyncStatusBadgeVariant = (
    status: SyncStatus,
  ): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case SyncStatus.SYNCED:
        return 'default'; // зелёный
      case SyncStatus.PENDING:
        return 'secondary'; // жёлтый
      case SyncStatus.FAILED:
        return 'destructive'; // красный
      default:
        return 'outline';
    }
  };

  // Текст для Badge
  const getSyncStatusLabel = (status: SyncStatus): string => {
    switch (status) {
      case SyncStatus.SYNCED:
        return 'Отправлено';
      case SyncStatus.PENDING:
        return 'Ожидает';
      case SyncStatus.FAILED:
        return 'Ошибка';
      default:
        return status;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h1 gradient-text-blue">Медикаменты</h1>
          <p className="text-muted-foreground mt-2 text-body-lg">
            История сканирований препаратов
          </p>
        </div>
        <Button
          onClick={handleRetryPending}
          disabled={retryPendingMutation.isPending}
          className="flex items-center gap-2"
        >
          <Send className={`h-4 w-4 ${retryPendingMutation.isPending ? 'animate-pulse' : ''}`} />
          {retryPendingMutation.isPending ? 'Отправка...' : 'Отправить неотправленные в 1С'}
        </Button>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle>Фильтры и поиск</CardTitle>
          <CardDescription>
            Используйте фильтры для поиска нужных сканирований
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Первая строка фильтров */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Поиск по GTIN */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Поиск по GTIN</label>
              <Input
                placeholder="04870004560161 (14 цифр)"
                value={searchGtin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 14);
                  setSearchGtin(value);
                }}
                maxLength={14}
              />
            </div>

            {/* Поиск по ИИН пациента */}
            <div className="space-y-2">
              <label className="text-sm font-medium">ИИН пациента</label>
              <Input
                placeholder="920101300123 (12 цифр)"
                value={searchPatientIin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                  setSearchPatientIin(value);
                }}
                maxLength={12}
              />
            </div>

            {/* Фильтр по сотруднику */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Сотрудник</label>
              <Select
                value={selectedEmployeeId}
                onValueChange={setSelectedEmployeeId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все сотрудники" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все сотрудники</SelectItem>
                  {!isLoadingEmployees &&
                    employeesData?.data.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.last_name} {employee.first_name}
                        {employee.middle_name ? ` ${employee.middle_name}` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Фильтр по статусу синхронизации */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Статус синхронизации</label>
              <Select
                value={selectedSyncStatus}
                onValueChange={setSelectedSyncStatus}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все статусы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="synced">Отправлено</SelectItem>
                  <SelectItem value="pending">Ожидает</SelectItem>
                  <SelectItem value="failed">Ошибка</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Сортировка */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Сортировка</label>
              <Select
                value={filters.sort || 'scanned_at:desc'}
                onValueChange={(value) =>
                  setFilters({ ...filters, sort: value, page: 1 })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scanned_at:desc">
                    Сначала новые
                  </SelectItem>
                  <SelectItem value="scanned_at:asc">
                    Сначала старые
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Вторая строка фильтров - даты */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Дата с
              </label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Дата по
              </label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            {/* Кнопки */}
            <div className="flex items-end gap-2 md:col-span-2">
              <Button onClick={handleApplyFilters} className="flex-1">
                <Search className="mr-2 h-4 w-4" />
                Применить
              </Button>
              <Button onClick={handleResetFilters} variant="outline">
                Сбросить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {data && !isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold gradient-text-blue">
                    {data.total}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Всего сканирований
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-tco-blue-50 flex items-center justify-center">
                  <Pill className="h-6 w-6 text-tco-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold gradient-text-blue">
                    {data.scans.length}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    На странице
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-success-50 flex items-center justify-center">
                  <Search className="h-6 w-6 text-success-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold gradient-text-blue">
                    {data.page}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Страница из {data.totalPages}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-info-50 flex items-center justify-center">
                  <Search className="h-6 w-6 text-info-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="hover-lift">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold gradient-text-blue">
                    {data.pageSize}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Записей на странице
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-warning-50 flex items-center justify-center">
                  <Search className="h-6 w-6 text-warning-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table Card */}
      <Card>
        <CardHeader>
          <CardTitle>История сканирований</CardTitle>
          <CardDescription>
            {data && `Показано ${data.scans.length} из ${data.total} записей`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={20} />
          ) : error ? (
            <ErrorState
              title="Ошибка загрузки данных"
              description={
                error instanceof Error
                  ? error.message
                  : 'Не удалось загрузить историю сканирований'
              }
              onAction={() => refetch()}
              actionLabel="Повторить"
            />
          ) : data && data.scans.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Препарат</TableHead>
                      <TableHead>Сотрудник</TableHead>
                      <TableHead>Пациент (ИИН)</TableHead>
                      <TableHead>Дата/время сканирования</TableHead>
                      <TableHead>Срок годности</TableHead>
                      <TableHead>Статус синхронизации</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.scans.map((scan) => (
                      <TableRow key={scan.id}>
                        {/* Препарат: GTIN + Serial */}
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium font-mono text-sm">
                              {scan.gtin}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              S/N: {scan.serial_number}
                            </p>
                            {scan.unique_identifier && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]" title={scan.unique_identifier}>
                                UID: {scan.unique_identifier.substring(0, 20)}...
                              </p>
                            )}
                          </div>
                        </TableCell>

                        {/* Сотрудник */}
                        <TableCell>{scan.employee_name}</TableCell>

                        {/* Пациент ИИН */}
                        <TableCell>
                          {scan.patient_iin ? (
                            <span className="font-mono text-sm">
                              {scan.patient_iin}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* Дата/время */}
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm">
                              {formatDateTime(scan.scanned_at)}
                            </p>
                            {scan.batch_number && (
                              <p className="text-xs text-muted-foreground">
                                Партия: {scan.batch_number}
                              </p>
                            )}
                          </div>
                        </TableCell>

                        {/* Срок годности */}
                        <TableCell>
                          {scan.expiry_date ? (
                            <div className="flex items-center gap-2">
                              <span className="text-sm">
                                {formatDate(scan.expiry_date)}
                              </span>
                              {isExpired(scan.expiry_date) && (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Истёк
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>

                        {/* Статус синхронизации */}
                        <TableCell>
                          <div className="space-y-1">
                            <Badge variant={getSyncStatusBadgeVariant(scan.sync_status)}>
                              {getSyncStatusLabel(scan.sync_status)}
                            </Badge>
                            {scan.sync_status === SyncStatus.FAILED && scan.sync_error && (
                              <p className="text-xs text-destructive truncate max-w-[200px]" title={scan.sync_error}>
                                {scan.sync_error}
                              </p>
                            )}
                            {scan.sync_status === SyncStatus.SYNCED && scan.synced_at && (
                              <p className="text-xs text-muted-foreground">
                                {formatDateTime(scan.synced_at)}
                              </p>
                            )}
                          </div>
                        </TableCell>

                        {/* Действия */}
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedScan(scan)}
                              title="Просмотреть детали"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {scan.sync_status === SyncStatus.FAILED && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRetrySync(scan.id)}
                                title="Повторить отправку в 1С"
                                disabled={retryMutation.isPending}
                              >
                                <RefreshCw className={`h-4 w-4 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          ) : (
            /* Empty State */
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-tco-blue-50 mb-4">
                <Pill className="h-8 w-8 text-tco-blue-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                Нет сканирований
              </h3>
              <p className="text-muted-foreground mb-4">
                Сканирования препаратов появятся здесь после их регистрации через мобильное приложение
              </p>
              {(searchGtin ||
                searchPatientIin ||
                selectedEmployeeId ||
                selectedSyncStatus !== 'all' ||
                dateFrom ||
                dateTo) && (
                <Button onClick={handleResetFilters} variant="outline">
                  Сбросить фильтры
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* TODO: ViewScanDialog будет создан отдельно */}
      {selectedScan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background p-6 rounded-lg max-w-2xl w-full mx-4">
            <h2 className="text-xl font-bold mb-4">Детали сканирования</h2>
            <div className="space-y-2 text-sm">
              <p><strong>ID:</strong> {selectedScan.id}</p>
              <p><strong>GTIN:</strong> {selectedScan.gtin}</p>
              <p><strong>Serial Number:</strong> {selectedScan.serial_number}</p>
              {selectedScan.unique_identifier && (
                <p><strong>Unique ID:</strong> {selectedScan.unique_identifier}</p>
              )}
              <p><strong>Сотрудник:</strong> {selectedScan.employee_name}</p>
              {selectedScan.patient_iin && (
                <p><strong>Пациент ИИН:</strong> {selectedScan.patient_iin}</p>
              )}
              <p><strong>Дата сканирования:</strong> {formatDateTime(selectedScan.scanned_at)}</p>
            </div>
            <Button onClick={() => setSelectedScan(null)} className="mt-4">
              Закрыть
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
